import React from 'react';

import MoreVertIcon from '@mui/icons-material/MoreVert';

import {
  Avatar,
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  IconButton,
  Menu,
  Typography,
} from 'components';

import { styled, useTheme } from 'third-party';

import { getAvatarName } from 'utils/helper';

const PointerBox = styled.div<{ isPointer: boolean }>`
  cursor: ${props => (props.isPointer ? 'pointer' : 'default')};
  user-select: none;
`;

const StripeWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 150px;
  height: 150px;
  overflow: hidden;
  pointer-events: none;
  z-index: 1;
`;

const StripeLabel = styled.div`
  position: absolute;
  top: 30px;
  right: -35px;
  width: 200px;
  padding: 8px 0;
  background-color: ${props => props.theme.custom.palette.secondary800};
  color: #ffffff;
  text-align: center;
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 1px;
  transform: rotate(45deg);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;

type Props<TItem> = {
  item: TItem;
  title: string;
  width?: string;
  height?: string;
  showAvatar?: boolean;
  avatarUrl?: string;
  description?: string | null;
  descriptionMaxLines?: number;
  subTitle?: React.ReactNode;
  menuItems?: (item: TItem, handleClose: () => void) => React.ReactNode;
  cardActions?: (item: TItem) => React.ReactNode;
  onClick?: (item: TItem) => void;
  children?: React.ReactNode;
  stripeText?: string;
  highlightQuery?: string;
};

const highlightText = (text: string, query: string): React.ReactNode => {
  if (!query.trim()) return text;
  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedQuery})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, index) =>
    regex.test(part) ? (
      <mark key={index} style={{ backgroundColor: '#ffff00' }}>
        {part}
      </mark>
    ) : (
      part
    ),
  );
};

export const CardBox = <TItem,>({
  item,
  title,
  subTitle,
  description,
  descriptionMaxLines = 2,
  width,
  height,
  showAvatar = true,
  avatarUrl,
  onClick,
  menuItems,
  cardActions,
  children,
  stripeText,
  highlightQuery,
}: Props<TItem>) => {
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const menu = menuItems?.(item, handleClose);
  return (
    <Card sx={{ position: 'relative' }}>
      {stripeText && (
        <StripeWrapper>
          <StripeLabel>{stripeText}</StripeLabel>
        </StripeWrapper>
      )}
      <PointerBox isPointer={!!onClick} onClick={() => onClick?.(item)}>
        <Box
          display="flex"
          flexDirection="column"
          flex={1}
          sx={{ width: width ?? 300, height: height ?? 220 }}
        >
          <CardHeader
            sx={{
              '& .MuiCardHeader-content': {
                overflow: 'hidden',
                minWidth: 0,
              },
            }}
            avatar={
              <Box>
                {showAvatar && !avatarUrl && (
                  <Avatar sx={{ bgcolor: theme.custom.palette.primary900 }}>
                    {getAvatarName(title)}
                  </Avatar>
                )}
                {showAvatar && avatarUrl && <Avatar src={avatarUrl} />}
              </Box>
            }
            action={
              menu && (
                <IconButton
                  aria-label="settings"
                  onClick={e => {
                    e.stopPropagation();
                    handleClick(e);
                  }}
                >
                  <MoreVertIcon />
                </IconButton>
              )
            }
            title={
              <Typography
                variant="h6"
                noWrap
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: '1rem',
                }}
              >
                {highlightQuery ? highlightText(title, highlightQuery) : title}
              </Typography>
            }
            subheader={
              subTitle && (
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: 'text.secondary',
                  }}
                >
                  {highlightQuery && typeof subTitle === 'string'
                    ? highlightText(subTitle, highlightQuery)
                    : subTitle}
                </Typography>
              )
            }
          />
          <Box
            display="flex"
            flexDirection="column"
            flex={1}
            justifyContent="space-between"
          >
            <CardContent style={{ padding: '0.5rem 1rem 0rem 1rem' }}>
              {description && (
                <Typography
                  variant="body2"
                  style={{
                    color: 'text.secondary',
                    display: '-webkit-box',
                    overflow: 'hidden',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: descriptionMaxLines,
                  }}
                >
                  {highlightQuery && typeof description === 'string'
                    ? highlightText(description, highlightQuery)
                    : description}
                </Typography>
              )}
              {children}
            </CardContent>
            {cardActions && (
              <CardActions disableSpacing onClick={e => e.stopPropagation()}>
                {cardActions(item)}
              </CardActions>
            )}
          </Box>
        </Box>
      </PointerBox>
      {menu && (
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          {menu}
        </Menu>
      )}
    </Card>
  );
};
